/**
 * Product Gallery Zoom & Slider
 * リッチUIのモーダル内ピンチ/ドラッグズーム + スライダー機能
 */

class ProductGalleryZoom {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.modal = document.getElementById(`CustomImageModal-${sectionId}`);
    this.zoomContainer = this.modal?.querySelector('.bb-modal__zoom-container');
    this.image = this.modal?.querySelector('.bb-modal__image');
    this.panzoom = null;
    this.currentScale = 1;
    this.minScale = 1;
    this.maxScale = 4;

    // スライダー関連
    this.images = [];
    this.currentIndex = 0;
    this.isTransitioning = false;

    // タッチスワイプ用
    this.touchStartX = 0;
    this.touchEndX = 0;

    if (this.modal && this.zoomContainer && this.image) {
      this.loadImages();
      this.init();
    }
  }

  loadImages() {
    const dataEl = document.getElementById(`ModalImagesData-${this.sectionId}`);
    if (dataEl) {
      try {
        this.images = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('Failed to parse modal images data:', e);
      }
    }
  }

  init() {
    this.bindZoomControls();
    this.bindSliderControls();
    this.bindKeyboard();
    this.bindImageLoad();
    this.bindTouchSwipe();
    this.updateCounter();
    this.updateThumbnails();
  }

  initPanzoom() {
    if (this.panzoom) {
      this.panzoom.destroy();
    }

    // 画像のtransformをリセット
    this.image.style.transform = '';

    this.panzoom = Panzoom(this.image, {
      maxScale: this.maxScale,
      minScale: this.minScale,
      startScale: 1,
      startX: 0,
      startY: 0,
      // contain: false - containmentを使用しない（ズームを妨げるため）
      cursor: 'grab',
      panOnlyWhenZoomed: true,
      animate: true,
      duration: 200,
      easing: 'ease-out'
    });

    this.currentScale = 1;

    // ホイールズーム
    this.zoomContainer.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

    // ズーム変更イベント
    this.image.addEventListener('panzoomchange', () => {
      this.updateScale();
    });

    // ダブルクリックでズームトグル
    this.image.addEventListener('dblclick', (e) => {
      e.preventDefault();
      if (this.currentScale > 1) {
        this.resetZoom();
      } else {
        this.panzoom.zoomToPoint(2.5, e);
        this.updateScale();
      }
    });
  }

  handleWheel(e) {
    if (!this.modal.classList.contains('is-open')) return;
    e.preventDefault();

    // ホイールの方向を取得（上 = ズームイン、下 = ズームアウト）
    const delta = e.deltaY > 0 ? -1 : 1;
    const zoomFactor = 0.15; // ズーム量

    // 新しいスケールを計算
    const currentScale = this.panzoom.getScale();
    let newScale = currentScale * (1 + delta * zoomFactor);

    // min/max制限
    newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

    // 中央基準でズーム（focal pointを指定しない = 中央）
    this.panzoom.zoom(newScale, { animate: false });
    this.updateScale();
  }

  bindZoomControls() {
    const zoomIn = this.modal.querySelector('.bb-modal__zoom-in');
    const zoomOut = this.modal.querySelector('.bb-modal__zoom-out');
    const zoomReset = this.modal.querySelector('.bb-modal__zoom-reset');

    zoomIn?.addEventListener('click', () => this.zoomIn());
    zoomOut?.addEventListener('click', () => this.zoomOut());
    zoomReset?.addEventListener('click', () => this.resetZoom());
  }

  bindSliderControls() {
    const prevBtn = this.modal.querySelector('.bb-modal__nav--prev');
    const nextBtn = this.modal.querySelector('.bb-modal__nav--next');
    const thumbnails = this.modal.querySelectorAll('.bb-modal__thumb');

    prevBtn?.addEventListener('click', () => this.prevImage());
    nextBtn?.addEventListener('click', () => this.nextImage());

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goToImage(index));
    });
  }

  bindKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('is-open')) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextImage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          this.zoomIn();
          break;
        case '-':
          e.preventDefault();
          this.zoomOut();
          break;
        case '0':
          e.preventDefault();
          this.resetZoom();
          break;
      }
    });
  }

  bindImageLoad() {
    this.image.addEventListener('load', () => {
      this.resetZoom();
      this.isTransitioning = false;
    });
  }

  bindTouchSwipe() {
    this.zoomContainer.addEventListener('touchstart', (e) => {
      // ズーム中はスワイプ無効
      if (this.currentScale > 1) return;
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.zoomContainer.addEventListener('touchend', (e) => {
      if (this.currentScale > 1) return;
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
  }

  handleSwipe() {
    const diff = this.touchStartX - this.touchEndX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextImage();
      } else {
        this.prevImage();
      }
    }
  }

  // スライダー機能
  prevImage() {
    if (this.isTransitioning || this.images.length <= 1) return;
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1;
    this.goToImage(newIndex);
  }

  nextImage() {
    if (this.isTransitioning || this.images.length <= 1) return;
    const newIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0;
    this.goToImage(newIndex);
  }

  goToImage(index) {
    if (this.isTransitioning || index === this.currentIndex || index < 0 || index >= this.images.length) return;

    this.isTransitioning = true;
    const prevIndex = this.currentIndex;
    this.currentIndex = index;

    // ズームをリセット
    if (this.panzoom) {
      this.panzoom.reset({ animate: false });
      this.currentScale = 1;
      this.updateScaleIndicator();
    }

    const imgData = this.images[index];
    if (!imgData) {
      this.isTransitioning = false;
      return;
    }

    // 画像を切り替え（フェードアニメーション）
    this.image.style.opacity = '0';

    // 新しい画像をプリロード
    const preloadImg = new Image();
    preloadImg.onload = () => {
      // プリロード完了後にsrcを設定してフェードイン
      setTimeout(() => {
        this.image.src = imgData.src;
        this.image.alt = imgData.alt || '';
        this.image.style.opacity = '1';
        // 隣接画像をバックグラウンドでプリロード
        this.preloadAdjacentImages(index);
      }, 100);
    };
    preloadImg.onerror = () => {
      // エラー時もフェードイン（元画像を表示）
      this.image.style.opacity = '1';
      this.isTransitioning = false;
    };
    preloadImg.src = imgData.src;

    this.updateCounter();
    this.updateThumbnails();
  }

  // 隣接画像のプリロード
  preloadAdjacentImages(currentIndex) {
    const indicesToPreload = [
      currentIndex - 1,
      currentIndex + 1,
      currentIndex + 2
    ];

    indicesToPreload.forEach(idx => {
      if (idx >= 0 && idx < this.images.length && this.images[idx]) {
        const img = new Image();
        img.src = this.images[idx].src;
      }
    });
  }

  updateCounter() {
    const current = this.modal.querySelector('.bb-modal__counter-current');
    if (current) {
      current.textContent = this.currentIndex + 1;
    }
  }

  updateThumbnails() {
    const thumbnails = this.modal.querySelectorAll('.bb-modal__thumb');
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle('is-active', index === this.currentIndex);
    });
  }

  // ズーム機能
  zoomIn() {
    if (!this.panzoom) return;
    const newScale = Math.min(this.currentScale * 1.5, this.maxScale);
    this.panzoom.zoom(newScale, { animate: true });
    this.updateScale();
  }

  zoomOut() {
    if (!this.panzoom) return;
    const newScale = Math.max(this.currentScale / 1.5, this.minScale);
    this.panzoom.zoom(newScale, { animate: true });
    this.updateScale();
  }

  resetZoom() {
    if (!this.panzoom) return;
    this.panzoom.reset({ animate: true });
    this.currentScale = 1;
    this.updateScaleIndicator();
  }

  updateScale() {
    if (!this.panzoom) return;
    const prevScale = this.currentScale;
    this.currentScale = this.panzoom.getScale();

    // 100%（またはそれ以下）に戻った時、位置を中央にリセット
    if (this.currentScale <= 1 && prevScale > 1) {
      this.panzoom.reset({ animate: true });
      this.currentScale = 1;
    }

    this.updateScaleIndicator();
  }

  updateScaleIndicator() {
    const indicator = this.modal.querySelector('.bb-modal__zoom-level');
    if (indicator) {
      indicator.textContent = `${Math.round(this.currentScale * 100)}%`;
    }

    const zoomOut = this.modal.querySelector('.bb-modal__zoom-out');
    if (zoomOut) {
      zoomOut.disabled = this.currentScale <= this.minScale;
    }

    const zoomIn = this.modal.querySelector('.bb-modal__zoom-in');
    if (zoomIn) {
      zoomIn.disabled = this.currentScale >= this.maxScale;
    }
  }

  // 指定インデックスで開く
  openAtIndex(index) {
    if (index >= 0 && index < this.images.length) {
      this.currentIndex = index;
      const imgData = this.images[index];
      if (imgData) {
        this.image.src = imgData.src;
        this.image.alt = imgData.alt || '';
      }
      this.updateCounter();
      this.updateThumbnails();
    }
  }

  onModalOpen(initialIndex = null) {
    // 初期インデックスが指定された場合
    if (initialIndex !== null && initialIndex >= 0 && initialIndex < this.images.length) {
      this.currentIndex = initialIndex;
      const imgData = this.images[initialIndex];
      if (imgData) {
        this.image.src = imgData.src;
        this.image.alt = imgData.alt || '';
      }
      this.updateCounter();
      this.updateThumbnails();
    }

    setTimeout(() => {
      this.initPanzoom();
      this.updateScaleIndicator();
    }, 100);

    // モーダルオープン時に全画像をプリロード（バックグラウンド）
    this.preloadAllImages();
  }

  // 全画像のプリロード
  preloadAllImages() {
    this.images.forEach((imgData, idx) => {
      if (imgData && imgData.src) {
        const img = new Image();
        img.src = imgData.src;
      }
    });
  }

  onModalClose() {
    if (this.panzoom) {
      this.panzoom.reset({ animate: false });
      this.currentScale = 1;
    }
    this.isTransitioning = false;
  }

  destroy() {
    if (this.panzoom) {
      this.panzoom.destroy();
      this.panzoom = null;
    }
  }
}

// グローバルに公開
window.ProductGalleryZoom = ProductGalleryZoom;
